import { createApi } from '@reduxjs/toolkit/query/react'
import type { AnswerValue, CalendarAnswer } from '@tunasong/models/calendar'
import baseQuery from './base-query.js'

/**
 * Check-in answers (GH #2814 M3), the calendar API's answer routes: an attendee answers an occurrence, attendees read
 * the roster, the organizer reminds. An occurrence is named by its event and its original start.
 */

export interface OccurrenceRef {
  eventId: string
  originalStart: string
}

export interface OccurrenceAnswers {
  answers: CalendarAnswer[]
  summary: { in: number; cannot: number; noReply: number }
}

const occurrencePath = ({ eventId, originalStart }: OccurrenceRef) =>
  `calendar/events/${eventId}/occurrences/${encodeURIComponent(originalStart)}`

const answersTag = ({ eventId, originalStart }: OccurrenceRef) => ({
  type: 'CalendarAnswers' as const,
  id: `${eventId}#${originalStart}`,
})

export const calendarAnswersApi = createApi({
  reducerPath: 'calendar-answers-api',
  baseQuery,
  tagTypes: ['CalendarAnswers'],
  endpoints: builder => ({
    listOccurrenceAnswers: builder.query<OccurrenceAnswers, OccurrenceRef>({
      query: ref => `${occurrencePath(ref)}/answers`,
      providesTags: (_result, _error, ref) => [answersTag(ref)],
    }),
    putOccurrenceAnswer: builder.mutation<
      { answer: CalendarAnswer },
      OccurrenceRef & { answer: AnswerValue; note?: string }
    >({
      query: ({ answer, note, ...ref }) => ({
        url: `${occurrencePath(ref)}/answer`,
        method: 'PUT',
        body: { answer, ...(note ? { note } : {}) },
      }),
      invalidatesTags: (_result, _error, { eventId, originalStart }) => [answersTag({ eventId, originalStart })],
    }),
    remindOccurrence: builder.mutation<{ reminded: number }, OccurrenceRef & { userIds?: string[] }>({
      query: ({ userIds, ...ref }) => ({
        url: `${occurrencePath(ref)}/remind`,
        method: 'POST',
        body: userIds ? { userIds } : {},
      }),
    }),
  }),
})

export const { useListOccurrenceAnswersQuery, usePutOccurrenceAnswerMutation, useRemindOccurrenceMutation } =
  calendarAnswersApi
